/**
 * Story player state machine (Instagram-stories grammar), kept pure so it is unit-tested:
 * one progress bar per segment, tap right → next, tap left → previous (or restart the segment
 * when you're already into it), hold → pause, release → resume.
 *
 * Two drivers:
 *  - 'timer'  — captions only: a segment ends when its estimated reading time elapses.
 *  - 'audio'  — server narration: progress comes from the media clock, completion from `ended`.
 */
export type PlayerDriver = 'timer' | 'audio';

export type PlayerState = {
  index: number;
  durations: number[];
  elapsedMs: number;
  paused: boolean; // explicit pause (button)
  held: boolean; // finger down
  buffering: boolean; // waiting for narration; independent of the fan's pause/hold
  endPending: boolean; // a voice ended at the pause boundary; advance only on resume
  done: boolean;
  driver: PlayerDriver;
  /** Bumped whenever the current segment (re)starts: voices restart on change. */
  epoch: number;
};

export type PlayerAction =
  | { type: 'tick'; dt: number }
  | { type: 'next' }
  | { type: 'prev' }
  | { type: 'hold' }
  | { type: 'release' }
  | { type: 'toggle' }
  | { type: 'segmentEnded'; index: number; epoch?: number }
  | { type: 'audioTime'; index: number; epoch: number; elapsedMs: number }
  | { type: 'buffer'; active: boolean }
  | { type: 'driver'; driver: PlayerDriver; durations?: number[] }
  | { type: 'restart' }
  | { type: 'goto'; index: number };

/** A segment's on-screen time when nobody is speaking: ~ reading pace, clamped. */
export function readingMs(text: string, lang: 'en' | 'hi' | 'zh-Hans' = 'en'): number {
  if (lang === 'zh-Hans') {
    const han = text.match(/\p{Script=Han}/gu)?.length ?? 0;
    const words = text.replace(/\p{Script=Han}/gu, ' ').match(/[\p{L}\p{N}]+/gu)?.length ?? 0;
    return Math.round(Math.min(18_000, Math.max(3_800, 900 + han * 180 + words * 290)));
  }
  const words = text.trim().split(/\s+/).filter(Boolean).length;
  const perWord = lang === 'hi' ? 330 : 290;
  return Math.round(Math.min(11_000, Math.max(3_800, 900 + words * perWord)));
}

/** Tap within this much of a segment's start goes to the previous segment (stories behaviour). */
export const PREV_GRACE_MS = 1200;

export function initPlayer(durations: number[], driver: PlayerDriver = 'timer', startAt = 0): PlayerState {
  return { index: Math.min(Math.max(0, startAt), Math.max(0, durations.length - 1)), durations, elapsedMs: 0, paused: false, held: false, buffering: false, endPending: false, done: durations.length === 0, driver, epoch: 0 };
}

const at = (s: PlayerState, index: number): PlayerState => {
  if (index >= s.durations.length) return { ...s, index: s.durations.length - 1, elapsedMs: s.durations.at(-1) ?? 0, done: true, endPending: false };
  return { ...s, index: Math.max(0, index), elapsedMs: 0, done: false, endPending: false, epoch: s.epoch + 1 };
};

export function playing(s: PlayerState): boolean {
  return !s.done && !s.paused && !s.held && !s.buffering;
}

const resumePending = (s: PlayerState) => s.endPending && playing(s) ? at(s, s.index + 1) : s;

export function playerReducer(s: PlayerState, a: PlayerAction): PlayerState {
  switch (a.type) {
    case 'tick': {
      if (!playing(s) || s.driver === 'audio') return s;
      const dur = s.durations[s.index] ?? 0;
      const elapsedMs = s.elapsedMs + Math.max(0, a.dt);
      return elapsedMs >= dur ? at(s, s.index + 1) : { ...s, elapsedMs };
    }
    case 'audioTime':
      if (!playing(s) || s.driver !== 'audio' || a.index !== s.index || a.epoch !== s.epoch || !Number.isFinite(a.elapsedMs)) return s;
      return { ...s, elapsedMs: Math.max(0, Math.min(a.elapsedMs, s.durations[s.index] ?? 0)) };
    case 'segmentEnded':
      // A late "ended" from a segment the fan already skipped must not skip another one.
      if (a.index !== s.index || a.epoch !== undefined && a.epoch !== s.epoch || s.done || s.driver !== 'audio') return s;
      return playing(s) ? at(s, s.index + 1) : { ...s, endPending: true };
    case 'buffer':
      return resumePending({ ...s, buffering: a.active });
    case 'next':
      return s.done ? s : at(s, s.index + 1);
    case 'prev':
      if (s.done) return at(s, s.durations.length - 1);
      return s.elapsedMs > PREV_GRACE_MS || s.index === 0 ? at(s, s.index) : at(s, s.index - 1);
    case 'hold':
      return { ...s, held: true };
    case 'release':
      return resumePending({ ...s, held: false });
    case 'toggle':
      if (s.done) return { ...at(s, 0), paused: false };
      return resumePending({ ...s, paused: !s.paused });
    case 'driver':
      // Switching voice restarts the current segment so speech and caption line up.
      return { ...at({ ...s, durations: a.durations ?? s.durations }, s.done ? 0 : s.index), driver: a.driver, buffering: false };
    case 'restart':
      return { ...at(s, 0), paused: false, held: false };
    case 'goto':
      return at(s, Math.min(Math.max(0, a.index), s.durations.length - 1));
  }
}

/** Fill (0–1) of each progress bar. */
export function progressBars(s: PlayerState): number[] {
  return s.durations.map((d, i) => (s.done || i < s.index ? 1 : i > s.index ? 0 : d > 0 ? Math.min(1, s.elapsedMs / d) : 0));
}

/** Split a segment into caption "pages" (sentences; Hindi ends with ।). */
export function captionPages(text: string): string[] {
  const parts = text
    .split(/(?<=[.!?।])\s+|(?<=[。！？])/u)
    .map((p) => p.trim())
    .filter(Boolean)
    .flatMap((sentence) => {
      // Exact substrings retain speech charIndex alignment in unspaced Chinese.
      if (!/\p{Script=Han}/u.test(sentence) || sentence.length <= 42) return [sentence];
      const pages: string[] = [];
      let rest = sentence;
      while (rest.length > 42) {
        const comma = Math.max(rest.lastIndexOf('，', 42), rest.lastIndexOf('；', 42), rest.lastIndexOf('、', 42));
        let end = comma >= 16 ? comma + 1 : 42;
        if (/[\uD800-\uDBFF]/u.test(rest[end - 1]!)) end--;
        pages.push(rest.slice(0, end).trim());
        rest = rest.slice(end).trimStart();
      }
      if (rest) pages.push(rest);
      return pages;
    });
  return parts.length ? parts : [text];
}

/** Speech offsets refer to the original text, not an assumed space between pages. */
export function pageAtCharacter(text: string, pages: readonly string[], charIndex: number): number {
  let from = 0;
  for (let i = 0; i < pages.length; i++) {
    const start = text.indexOf(pages[i]!, from);
    if (start < 0) continue;
    from = start + pages[i]!.length;
    const next = i + 1 < pages.length ? text.indexOf(pages[i + 1]!, from) : text.length;
    if (charIndex < next || i === pages.length - 1) return i;
  }
  return Math.max(0, pages.length - 1);
}

/** Which caption page is showing at `fraction` of the segment, weighted by page length. */
export function pageAt(pages: string[], fraction: number): number {
  const total = pages.reduce((n, p) => n + p.length, 0) || 1;
  let acc = 0;
  for (let i = 0; i < pages.length; i++) {
    acc += pages[i]!.length / total;
    if (fraction < acc) return i;
  }
  return Math.max(0, pages.length - 1);
}
